"use package";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import {
  FilterCommission,
  CommissionForm,
  CommissionSettlementForm,
  UseCommissionProps,
} from "@/types";
import { toast } from "react-toastify";
import {
  fetchFullCommission,
  fetchNormalCommmission,
  normalCommissionSettlement,
} from "@/store/modules/fullCommission/thunks";
import { useAuth } from "@/context/AuthContext";
import { normalCommissionSchema } from "@/validations";
import { useMemo, useState } from "react";
import { useForm, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

const useNormalCommission = ({ filter, onSuccess }: UseCommissionProps) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { currency } = useAuth();

  const [searchDataNormalCommission, setSearchDataNormalCommission] =
    useState<FilterCommission | null>(null);

  const [isSettlement, setIsSettlement] = useState(false);

  const [hasSearched, setHasSearched] = useState(false);

  const resolver = useMemo(() => {
    return yupResolver(normalCommissionSchema, {
      context: { isSettlement },
    }) as Resolver<CommissionForm>;
  }, [isSettlement]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CommissionForm>({
    resolver,
    context: { isSettlement },
    defaultValues: {
      employee_id: "",
      startDate: undefined,
      endDate: undefined,
      payment_method_id: null,
      paid_at: null,
    },
  });

  const { normalCommission, loading, error } = useSelector(
    (state: any) => state.fullCommission,
  );
  const dispatch: AppDispatch = useDispatch();

  const handleSubmitFilter = async (data: FilterCommission) => {
    setIsSettlement(false);
    const { startDate, endDate, employee_id } = data;

    if (!startDate || !endDate) {
      toast.error("Preencha o intervalo de datas!");
      return;
    }

    const resetParams = { ...params, page: 1 };

    const payload: Record<string, any> = {
      ...resetParams,
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
      employee_id: employee_id,
    };

    try {
      const result = await dispatch(fetchNormalCommmission(payload)).unwrap();
      setSearchDataNormalCommission(data);

      setCurrentPagerPurchaseVouchers(1);
      setCurrentPagerPendingNormalCommission(1);
    } catch (err: any) {
      if (err?.errors) {
        Object.values(err.errors)
          .flat()
          .forEach((msg: any) => {
            toast.error(msg);
          });
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Erro ao buscar dados financeiros.");
      }
    }
  };

  const handleSubmitNormalCommissionSettlement = async (
    data: CommissionSettlementForm,
  ) => {
    setIsSettlement(true);

    const payload: Record<string, any> = {
      start_date: data.startDate.toISOString().split("T")[0],
      end_date: data.endDate.toISOString().split("T")[0],
      employee_id: data.employee_id,
      paid_at: data.paid_at ? data.paid_at.toISOString().split("T")[0] : null,
      payment_method_id: data.payment_method_id,
    };

    try {
      await dispatch(normalCommissionSettlement(payload)).unwrap();

      reset();
      if (onSuccess) onSuccess();

      const startDate = data.startDate.toISOString().split("T")[0];
      const endDate = data.endDate.toISOString().split("T")[0];

      // persist date range into global params so subsequent requests reflect it
      try {
        updateParams &&
          updateParams({ start_date: startDate, end_date: endDate } as any);
      } catch (e) {
        // ignore
      }

      dispatch(
        fetchFullCommission({
          ...params,
          start_date: startDate,
          end_date: endDate,
        } as any),
      );

      //dispatch(fetchNormalCommmission(payload));
    } catch (err: any) {
      if (err?.errors) {
        Object.values(err.errors)
          .flat()
          .forEach((msg: any) => toast.error(msg));
      } else if (err?.message) {
        toast.error(err.message);
      }
    }
  };

  const onSubmitNormalCommission = (data: any) => {
    if (isSettlement) {
      setIsSettlement(true);
      handleSubmitNormalCommissionSettlement(data);
    } else {
      setIsSettlement(false);
      handleSubmitFilter(data);
      setHasSearched(true);
    }
  };

  const totalItemsrPurchaseVouchers =
    normalCommission?.data?.employee_debits?.total ?? 0;
  const itemsPerPagerPurchaseVouchers =
    normalCommission?.data?.employee_debits?.per_page ?? 10;
  const totalPagesrPurchaseVouchers =
    normalCommission?.data?.employee_debits?.last_page ?? 1;

  const [currentPagerPurchaseVouchers, setCurrentPagerPurchaseVouchers] =
    useState(1);

  const handlePageChangePurchaseVouchers = async (page: number) => {
    setCurrentPagerPurchaseVouchers(page);

    if (
      searchDataNormalCommission &&
      searchDataNormalCommission.startDate &&
      searchDataNormalCommission.endDate
    ) {
      const payload: Record<string, any> = {
        ...params,
        page: page,
        start_date: searchDataNormalCommission.startDate
          .toISOString()
          .split("T")[0],
        end_date: searchDataNormalCommission.endDate
          .toISOString()
          .split("T")[0],
        employee_id: searchDataNormalCommission.employee_id,
      };

      await dispatch(fetchNormalCommmission(payload)).unwrap();
    }
  };

  const totalItemsrPendingNormalCommission =
    normalCommission?.data?.commissions?.total ?? 0;
  const itemsPerPagerPendingNormalCommission =
    normalCommission?.data?.commissions?.per_page ?? 10;
  const totalPagesrPendingNormalCommission =
    normalCommission?.data?.commissions?.last_page ?? 1;

  const [
    currentPagerPendingNormalCommission,
    setCurrentPagerPendingNormalCommission,
  ] = useState(1);

  const handlePageChangePendingNormalCommission = async (page: number) => {
    setCurrentPagerPendingNormalCommission(page);

    if (
      searchDataNormalCommission &&
      searchDataNormalCommission.startDate &&
      searchDataNormalCommission.endDate
    ) {
      const payload: Record<string, any> = {
        ...params,
        page: page,
        start_date: searchDataNormalCommission.startDate
          .toISOString()
          .split("T")[0],
        end_date: searchDataNormalCommission.endDate
          .toISOString()
          .split("T")[0],
        employee_id: searchDataNormalCommission.employee_id,
      };

      try {
        await dispatch(fetchNormalCommmission(payload)).unwrap();
      } catch (err: any) {
        console.error("Erro ao buscar comissionamento normal:", err);
      }
    }
  };

  return {
    normalCommission,
    loading,
    error,
    handleSubmitFilter,
    currentPagerPurchaseVouchers,
    handlePageChangePurchaseVouchers,
    itemsPerPagerPurchaseVouchers,
    totalItemsrPurchaseVouchers,
    totalPagesrPurchaseVouchers,
    totalItemsrPendingNormalCommission,
    itemsPerPagerPendingNormalCommission,
    totalPagesrPendingNormalCommission,
    currentPagerPendingNormalCommission,
    handlePageChangePendingNormalCommission,
    currency,
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    errors,
    isSubmitting,
    searchDataNormalCommission,
    handleSubmitNormalCommissionSettlement,
    onSubmitNormalCommission,
    hasSearched,
    setIsSettlement,
    // expose helpers
    setError,
  };
};

export default useNormalCommission;
